import { apiClient } from './Api';
import { VOUCHER_ROUTE_API } from '../constants';

export const LIST_SEPARATOR = '|';
const DATE_RX = /^\d{4}-\d{2}-\d{2}$/;
const DATETIME_RX = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/;
const TIME_RX = /^([01][0-9]|2[0-3]):([0-5][0-9])$/;

const bool = (v) => v === '1' || String(v).toLowerCase() === 'true';
const enumRule = (allowed) => (v) => allowed.includes(v) || `must be one of: ${allowed.join(', ')}`;
const intRule = (min) => (v) => {
  if (!/^\d+$/.test(v)) return 'must be a whole number';
  if (min !== undefined && Number(v) < min) return `must be >= ${min}`;
  return true;
};
const boolRule = (v) => ['', '0', '1', 'true', 'false'].includes(String(v).toLowerCase()) || 'must be 1 or 0';
const dateRule = (v) => DATE_RX.test(v) || 'must be YYYY-MM-DD';
const dateTimeRule = (v) => DATETIME_RX.test(v) || 'must be YYYY-MM-DD HH:mm';

// Single source of truth. The sample CSV, the in-panel guide and the row
// validator are all derived from this list, so they can never drift apart.
export const COLUMNS = [
  { key: 'code', header: 'code', required: false, help: 'Your own code (A-Z 0-9). Blank = auto-generated.' },
  { key: 'name', header: 'name', required: true, help: 'Voucher name shown to the user.' },
  { key: 'description', header: 'description', required: false, help: 'Free text.' },
  { key: 'group_name', header: 'group_name', required: true, help: 'Batch label, ties this upload together for export.' },
  { key: 'voucher_type', header: 'voucher_type', required: true, help: 'public | private | public_open | in_store. Use private for a date-range cohort.', validate: enumRule(['public', 'private', 'public_open', 'in_store']) },
  { key: 'active', header: 'active', required: true, help: '1 = live, 0 = off.', validate: boolRule },
  { key: 'auto_apply', header: 'auto_apply', required: false, help: '1 = applied automatically at checkout without the buyer entering the code (best eligible one wins). 0 = buyer must apply it. Auto-apply only works for product-price discounts, not in_store.', validate: boolRule },
  { key: 'discount_type', header: 'discount_type', required: true, help: 'percentage | fixed.', validate: enumRule(['percentage', 'fixed']) },
  { key: 'discount_value', header: 'discount_value', required: true, help: 'If percentage: 1-100. If fixed: Rupiah (50000, no separators).' },
  { key: 'max_discount_rp', header: 'max_discount_rp', required: false, help: 'Rupiah ceiling. Only for percentage. Leave blank for fixed.' },
  { key: 'deduct_type', header: 'deduct_type', required: true, help: 'product_price | courier_price.', validate: enumRule(['product_price', 'courier_price']) },
  { key: 'minimum_purchase', header: 'minimum_purchase', required: false, help: 'Rupiah. Blank = no minimum.' },
  { key: 'is_cashback', header: 'is_cashback', required: false, help: '1 = cashback to balance, 0 = direct discount.', validate: boolRule },
  { key: 'limit', header: 'limit', required: false, help: 'Total redemptions across all users. Blank = unlimited.' },
  { key: 'limit_per_user', header: 'limit_per_user', required: false, help: 'Max redemptions by one user. Blank = unlimited.', validate: (v) => v === '' || intRule(1)(v) },
  { key: 'daily_limit', header: 'daily_limit', required: false, help: 'Max redemptions per day. Blank = unlimited.', validate: (v) => v === '' || intRule(1)(v) },
  { key: 'card_limit', header: 'card_limit', required: false, help: 'Max redemptions per card. Blank = unlimited.', validate: (v) => v === '' || intRule(0)(v) },
  { key: 'disposable_voucher', header: 'disposable_voucher', required: false, help: '1 = usable once per user, 0 = reusable.', validate: boolRule },
  { key: 'daily_start_time', header: 'daily_start_time', required: false, help: 'HH:mm, earliest usable time of day. Blank = 00:00.', validate: (v) => v === '' || TIME_RX.test(v) || 'must be HH:mm' },
  { key: 'started_at', header: 'started_at', required: false, help: 'YYYY-MM-DD HH:mm validity start. Blank = immediately.', validate: (v) => v === '' || dateTimeRule(v) },
  { key: 'ended_at', header: 'ended_at', required: false, help: 'YYYY-MM-DD HH:mm validity end. Blank = no expiry.', validate: (v) => v === '' || dateTimeRule(v) },
  { key: 'reg_start', header: 'reg_start', required: false, help: 'YYYY-MM-DD. Cohort: user registered on/after this. Needs voucher_type = private.', validate: (v) => v === '' || dateRule(v) },
  { key: 'reg_end', header: 'reg_end', required: false, help: 'YYYY-MM-DD. Cohort: user registered on/before this. Needs voucher_type = private.', validate: (v) => v === '' || dateRule(v) },
  { key: 'platform_specifications', header: 'platform_specifications', required: false, help: 'Only use for: All | App | Web. Blank or All = all platforms.', validate: (v) => v === '' || ['all', 'app', 'web'].includes(String(v).toLowerCase()) || 'must be All, App or Web' },
  { key: 'listing_standard', header: 'listing_standard', required: false, help: '1 / 0.', validate: boolRule },
  { key: 'listing_pre_order', header: 'listing_pre_order', required: false, help: '1 / 0.', validate: boolRule },
  { key: 'listing_pre_verified', header: 'listing_pre_verified', required: false, help: '1 / 0.', validate: boolRule },
  { key: 'terms', header: 'terms', required: false, help: 'Terms text.' },
  { key: 'instructions', header: 'instructions', required: false, help: 'Usage instructions text.' },
  { key: 'sku', header: 'sku', required: false, help: `One or more SKUs separated by "${LIST_SEPARATOR}". Voucher usable only on these. Blank = all products.` },
];

const EXAMPLE_ROWS = [
  { name: 'Ramadan 10%', group_name: 'RAMADAN25', voucher_type: 'public', active: '1', discount_type: 'percentage', discount_value: '10', max_discount_rp: '150000', deduct_type: 'product_price', minimum_purchase: '500000' },
  { name: 'New Year 50K', group_name: 'NY25', voucher_type: 'private', active: '1', discount_type: 'fixed', discount_value: '50000', deduct_type: 'product_price', reg_start: '2026-01-01', reg_end: '2026-03-31', sku: `ABC123${LIST_SEPARATOR}DEF456` },
];

const csvField = (v) => {
  const s = v === undefined || v === null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function buildSampleCsv() {
  const headers = COLUMNS.map((c) => c.header);
  const lines = [headers.join(',')];
  EXAMPLE_ROWS.forEach((ex) => {
    lines.push(COLUMNS.map((c) => csvField(ex[c.key])).join(','));
  });
  return lines.join('\n');
}

export function downloadSampleCsv() {
  const blob = new Blob([buildSampleCsv()], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'voucher-bulk-sample.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// RFC-4180-ish parser: handles quoted fields, escaped quotes and newlines
// inside quotes. Intra-cell lists use LIST_SEPARATOR, never the comma.
export function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  const src = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') { field += '"'; i += 1; } else { inQuotes = false; }
      } else { field += ch; }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field); field = '';
    } else if (ch === '\n') {
      row.push(field); field = ''; rows.push(row); row = [];
    } else {
      field += ch;
    }
  }
  row.push(field);
  rows.push(row);
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

export function rowsToObjects(matrix) {
  if (!matrix.length) return { headers: [], objects: [], unknown: [] };
  const headers = matrix[0].map((h) => h.trim());
  const known = COLUMNS.map((c) => c.header);
  const unknown = headers.filter((h) => h && !known.includes(h));
  const objects = matrix.slice(1).map((cells) => {
    const obj = {};
    headers.forEach((h, idx) => { obj[h] = (cells[idx] || '').trim(); });
    return obj;
  });
  return { headers, objects, unknown };
}

export function validateRows(objects) {
  const errors = [];
  const seenCodes = {};
  objects.forEach((obj, i) => {
    const rowNo = i + 2; // header is row 1
    COLUMNS.forEach((col) => {
      const val = obj[col.header] || '';
      if (col.required && val === '') {
        errors.push({ row: rowNo, column: col.header, reason: 'required, cannot be blank' });
        return;
      }
      if (val !== '' && col.validate) {
        const res = col.validate(val);
        if (res !== true) errors.push({ row: rowNo, column: col.header, reason: res });
      }
    });
    // discount_value depends on discount_type
    const dType = obj.discount_type;
    const dVal = obj.discount_value;
    if (dVal !== '' && dType === 'percentage') {
      if (!/^\d+$/.test(dVal) || Number(dVal) < 1 || Number(dVal) > 100) {
        errors.push({ row: rowNo, column: 'discount_value', reason: 'percentage must be a whole number 1-100' });
      }
    } else if (dVal !== '' && dType === 'fixed') {
      if (!/^\d+$/.test(dVal) || Number(dVal) < 1) {
        errors.push({ row: rowNo, column: 'discount_value', reason: 'fixed must be a whole Rupiah number' });
      }
    }
    if (obj.max_discount_rp && dType === 'fixed') {
      errors.push({ row: rowNo, column: 'max_discount_rp', reason: 'only allowed when discount_type = percentage' });
    }
    // cohort needs private
    if ((obj.reg_start || obj.reg_end) && obj.voucher_type !== 'private') {
      errors.push({ row: rowNo, column: 'voucher_type', reason: 'reg_start/reg_end require voucher_type = private' });
    }
    if (obj.reg_start && obj.reg_end && obj.reg_start > obj.reg_end) {
      errors.push({ row: rowNo, column: 'reg_end', reason: 'reg_end must be on/after reg_start' });
    }
    // duplicate code within the file
    if (obj.code) {
      if (seenCodes[obj.code]) errors.push({ row: rowNo, column: 'code', reason: `duplicate of row ${seenCodes[obj.code]}` });
      else seenCodes[obj.code] = rowNo;
    }
  });
  return errors;
}

export function rowToVoucherPayload(obj) {
  const p = {
    name: obj.name,
    description: obj.description || undefined,
    group_name: obj.group_name,
    voucher_type: obj.voucher_type,
    franchise: 'kard_avenue',
    active: bool(obj.active),
    auto_apply: bool(obj.auto_apply),
    type: obj.discount_type,
    amount: Number(obj.discount_value),
    deduct_type: obj.deduct_type,
    is_cashback: bool(obj.is_cashback),
    disposable_voucher: bool(obj.disposable_voucher),
    daily_start_time: `${obj.daily_start_time || '00:00'}:00`,
  };
  if (obj.code) p.code = obj.code;
  if (obj.max_discount_rp) p.max_amount = Number(obj.max_discount_rp);
  if (obj.minimum_purchase) p.minimum_purchase = Number(obj.minimum_purchase);
  if (obj.limit) p.limit = Number(obj.limit);
  if (obj.limit_per_user) p.limit_per_user = Number(obj.limit_per_user);
  if (obj.daily_limit) p.daily_limit = Number(obj.daily_limit);
  if (obj.card_limit) p.card_limit = Number(obj.card_limit);
  if (obj.started_at) p.started_at = obj.started_at;
  if (obj.ended_at) p.ended_at = obj.ended_at;
  const plat = String(obj.platform_specifications || '').toLowerCase();
  if (plat === 'app') p.platform_specifications = 'APP';
  else if (plat === 'web') p.platform_specifications = 'WEB';
  ['listing_standard', 'listing_pre_order', 'listing_pre_verified'].forEach((k) => { p[k] = bool(obj[k]); });
  if (obj.terms) p.terms = obj.terms;
  if (obj.instructions) p.instructions = obj.instructions;
  return p;
}

function unwrap(res) {
  const d = res && res.data ? res.data : {};
  return d.data !== undefined ? d.data : d;
}

async function resolveSkuToVariantIds(voucherId, skuCell) {
  const skus = skuCell.split(LIST_SEPARATOR).map((s) => s.trim()).filter(Boolean);
  const ids = [];
  const missing = [];
  for (let s = 0; s < skus.length; s += 1) {
    const sku = skus[s];
    const found = [];
    let page = 1;
    let lastPage = 1;
    do {
      // eslint-disable-next-line no-await-in-loop
      const res = await apiClient().get(`${VOUCHER_ROUTE_API}/${voucherId}/voucher_variants`, {
        params: { exclude: true, keyword: sku, per_page: 50, page },
      });
      const body = unwrap(res);
      const items = body.data || body || [];
      items.forEach((v) => { if (v && v.SKU === sku) found.push(v.id); });
      lastPage = (body.meta && body.meta.last_page) || body.last_page || page;
      page += 1;
    } while (page <= lastPage);
    if (!found.length) missing.push(sku);
    else found.forEach((id) => ids.push(id));
  }
  return { ids: Array.from(new Set(ids)), missing };
}

async function fetchUserIdsInRange(regStart, regEnd) {
  const ids = [];
  let page = 1;
  let lastPage = 1;
  do {
    // eslint-disable-next-line no-await-in-loop
    const res = await apiClient().get('admins/users', {
      params: { start_at: regStart || undefined, end_at: regEnd || undefined, per_page: 100, page },
    });
    const body = unwrap(res);
    const items = body.data || body || [];
    items.forEach((u) => ids.push(u.id));
    lastPage = (body.meta && body.meta.last_page) || body.last_page || page;
    page += 1;
  } while (page <= lastPage);
  return ids;
}

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// Validate-all-then-execute. Not a DB transaction: on a mid-batch failure the
// caller is handed every created id so it can roll back.
export async function runBulk(objects, onProgress) {
  const results = [];
  for (let i = 0; i < objects.length; i += 1) {
    const obj = objects[i];
    const rowNo = i + 2;
    const result = { row: rowNo, code: obj.code || '(auto)', voucherId: null, status: 'ok', warnings: [] };
    try {
      const createRes = await apiClient().post(VOUCHER_ROUTE_API, rowToVoucherPayload(obj));
      const voucher = unwrap(createRes);
      result.voucherId = voucher.id;
      result.code = voucher.code || result.code;

      if (obj.sku) {
        const { ids, missing } = await resolveSkuToVariantIds(voucher.id, obj.sku);
        if (ids.length) {
          await apiClient().post(`${VOUCHER_ROUTE_API}/${voucher.id}/voucher_variants`, { product_variants: ids });
        }
        if (missing.length) result.warnings.push(`SKU not found: ${missing.join(', ')}`);
      }

      if (obj.reg_start || obj.reg_end) {
        const userIds = await fetchUserIdsInRange(obj.reg_start, obj.reg_end);
        if (userIds.length) {
          const batches = chunk(userIds, 200);
          for (let b = 0; b < batches.length; b += 1) {
            await apiClient().post(`${VOUCHER_ROUTE_API}/uservouchers`, { voucher_id: voucher.id, user_id: batches[b] });
          }
          result.warnings.push(`assigned to ${userIds.length} users in range`);
        } else {
          result.warnings.push('no users matched the registration range');
        }
      }
    } catch (err) {
      result.status = 'failed';
      const data = err && err.response && err.response.data;
      result.error = (data && (data.message || (data.error && data.error.message))) || (err && err.message) || 'request failed';
    }
    results.push(result);
    if (onProgress) onProgress(i + 1, objects.length, result);
    if (result.status === 'failed') break;
  }
  return results;
}

export async function rollback(results) {
  const created = results.filter((r) => r.voucherId);
  for (let i = 0; i < created.length; i += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await apiClient().delete(`${VOUCHER_ROUTE_API}/${created[i].voucherId}`);
    } catch (e) { /* best effort */ }
  }
  return created.length;
}
